/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { Skeleton } from "@mui/material";
import { formatNumber } from "../../utils/numberFormatter";
import {
  GuestTopRestaurantsCard,
  GuestTopRestaurantsEmptyState,
  GuestTopRestaurantsErrorState,
  GuestTopRestaurantsHeader,
  GuestTopRestaurantsIconWrap,
  GuestTopRestaurantsItem,
  GuestTopRestaurantsItemAmount,
  GuestTopRestaurantsItemIndex,
  GuestTopRestaurantsItemMeta,
  GuestTopRestaurantsItemName,
  GuestTopRestaurantsList,
  GuestTopRestaurantsLoadingWrap,
  GuestTopRestaurantsRetryButton,
  GuestTopRestaurantsTitle,
} from "./styles";

const formatCurrency = (value) => `$${formatNumber(value)}`;

const LoadingState = () => (
  <GuestTopRestaurantsLoadingWrap data-testid="guest-top-restaurants-loader">
    <Skeleton variant="text" width="55%" height={28} />
    <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: '12px' }} />
    <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: '12px' }} />
    <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: '12px' }} />
  </GuestTopRestaurantsLoadingWrap>
);

export const GuestTopRestaurants = ({ guestTopRestaurants, isLoading, isError, onRetry }) => {
  if (isLoading) {
    return (
      <GuestTopRestaurantsCard data-testid="guest-top-restaurants-card">
        <LoadingState />
      </GuestTopRestaurantsCard>
    );
  }

  if (isError) {
    return (
      <GuestTopRestaurantsCard data-testid="guest-top-restaurants-card">
        <GuestTopRestaurantsHeader>
          <GuestTopRestaurantsIconWrap>
            <EmojiEventsOutlinedIcon sx={{ fontSize: '16px' }} />
          </GuestTopRestaurantsIconWrap>
          <GuestTopRestaurantsTitle>TOP RESTAURANTS</GuestTopRestaurantsTitle>
        </GuestTopRestaurantsHeader>
        <GuestTopRestaurantsErrorState data-testid="guest-top-restaurants-error-state">
          Failed to load top restaurants.
          <GuestTopRestaurantsRetryButton
            variant="outlined"
            onClick={onRetry}
            data-testid="guest-top-restaurants-retry-button"
          >
            Retry
          </GuestTopRestaurantsRetryButton>
        </GuestTopRestaurantsErrorState>
      </GuestTopRestaurantsCard>
    );
  }

  const restaurants = guestTopRestaurants || [];

  return (
    <GuestTopRestaurantsCard data-testid="guest-top-restaurants-card">
      <GuestTopRestaurantsHeader>
        <GuestTopRestaurantsIconWrap>
          <EmojiEventsOutlinedIcon sx={{ fontSize: '16px' }} />
        </GuestTopRestaurantsIconWrap>
        <GuestTopRestaurantsTitle>TOP RESTAURANTS</GuestTopRestaurantsTitle>
      </GuestTopRestaurantsHeader>

      {restaurants.length ? (
        <GuestTopRestaurantsList data-testid="guest-top-restaurants-list">
          {restaurants.map((restaurant, index) => (
            <GuestTopRestaurantsItem
              key={restaurant?.restaurant_id || `${restaurant?.restaurant_name}-${index}`}
              data-testid={`guest-top-restaurants-item-${restaurant?.restaurant_id || index}`}
            >
              <GuestTopRestaurantsItemIndex>{index + 1}</GuestTopRestaurantsItemIndex>

              <GuestTopRestaurantsItemMeta>
                <GuestTopRestaurantsItemName data-testid="guest-top-restaurants-name">
                  {restaurant?.restaurant_name || '-'}
                </GuestTopRestaurantsItemName>
                <GuestTopRestaurantsItemAmount data-testid="guest-top-restaurants-summary">
                  {`${restaurant?.total_visits ?? 0} visits · ${formatCurrency(restaurant?.amount)}`}
                </GuestTopRestaurantsItemAmount>
              </GuestTopRestaurantsItemMeta>
            </GuestTopRestaurantsItem>
          ))}
        </GuestTopRestaurantsList>
      ) : (
        <GuestTopRestaurantsEmptyState data-testid="guest-top-restaurants-empty-state">
          No top restaurants found.
        </GuestTopRestaurantsEmptyState>
      )}
    </GuestTopRestaurantsCard>
  );
};
